import Phaser from 'phaser';

import { StrokeRewardRenderer } from '../rendering/StrokeRewardRenderer';
import { StrokeGuideRenderer } from '../rendering/StrokeGuideRenderer';
import { StrokeDebugRenderer } from '../rendering/strokeDebugRenderer';
import { StrokeGeometry } from '../utils/StrokeGeometry';

import { StrokeEvaluator, CheckpointSequence } from './StrokeEvaluator';

import type { TracingGameStroke } from '../../types';

type StrokesConfig = {
	scene: Phaser.Scene;
	parent: Phaser.GameObjects.Container;
	debugParent: Phaser.GameObjects.Container;
	rewardParent: Phaser.GameObjects.Container;
	rewardTextureKey: string;
	data: [TracingGameStroke, ...TracingGameStroke[]];
	debug: boolean;
	drawingAreaWidth: number;
	drawingAreaHeight: number;
	checkpointMaxDistance: number;
	maxInkMultiplier: number;
	color?: string;
	opacity?: number;
};

type StrokeState = {
	geometry: StrokeGeometry;
	data: TracingGameStroke;
	progress: StrokeEvaluator;
};

const CHECKPOINT_SPACING_RATIO = 0.1;

export class Strokes {
	private readonly states: [StrokeState, ...StrokeState[]];
	private readonly progress: CheckpointSequence;
	private readonly rewardRenderer: StrokeRewardRenderer;
	private readonly guideRenderer: StrokeGuideRenderer;
	private readonly debugRenderer?: StrokeDebugRenderer;
	private readonly maxInkMultiplier: number;
	private readonly checkpointMaxDistance: number;

	constructor({
		scene,
		parent,
		debugParent,
		rewardParent,
		rewardTextureKey,
		data,
		debug,
		drawingAreaWidth,
		drawingAreaHeight,
		checkpointMaxDistance,
		maxInkMultiplier,
		color,
		opacity,
	}: StrokesConfig) {
		this.maxInkMultiplier = maxInkMultiplier;
		this.checkpointMaxDistance = checkpointMaxDistance;
		const checkpointSpacing = Math.min(drawingAreaWidth, drawingAreaHeight) * CHECKPOINT_SPACING_RATIO;
		const createState = (stroke: TracingGameStroke): StrokeState => {
			const geometry = new StrokeGeometry(stroke, drawingAreaWidth, drawingAreaHeight, checkpointSpacing);

			return {
				geometry,
				data: stroke,
				progress: new StrokeEvaluator(
					geometry.checkpoints,
					checkpointMaxDistance,
					undefined,
					geometry.trackingPath,
				),
			};
		};
		const [firstStroke, ...remainingStrokes] = data;

		this.states = [createState(firstStroke), ...remainingStrokes.map(createState)];
		this.progress = new CheckpointSequence(
			this.states.map(({ progress }) => progress) as [StrokeEvaluator, ...StrokeEvaluator[]],
		);

		this.guideRenderer = new StrokeGuideRenderer({
			scene,
			parent,
			paths: this.states.map(({ geometry }) => geometry.path),
			drawingAreaWidth,
			drawingAreaHeight,
			color,
			opacity,
		});

		this.guideRenderer.render(this.progress.getActiveIndex());

		this.rewardRenderer = new StrokeRewardRenderer({
			scene,
			parent: rewardParent,
			textureKey: rewardTextureKey,
			strokes: this.states.map(({ geometry, progress }) => {
				let distance = 0;
				return {
					path: geometry.path,
					length: geometry.length,
					checkpoints: geometry.checkpoints.flatMap((checkpoint, index) => {
						distance += checkpoint.distanceFromPrevious;
						return progress.isScoringCheckpoint(index) ? [{ index, distance }] : [];
					}),
				};
			}),
		});

		if (debug) {
			this.debugRenderer = new StrokeDebugRenderer({
				scene,
				parent: debugParent,
				strokes: this.states.map(({ geometry, progress }) => ({ checkpoints: geometry.checkpoints, progress })),
				checkpointMaxDistance,
			});
			this.debugRenderer?.render(this.progress.getActiveIndex());
		}
	}

	getActiveDistanceTo(point: Phaser.Types.Math.Vector2Like) {
		return this.getActiveState().geometry.getDistanceTo(point);
	}

	getActiveStartPosition() {
		return this.getActiveState().geometry.getStartPoint();
	}

	getActiveStartDirection() {
		return this.getActiveState().geometry.checkpoints[0].tangent;
	}

	getActiveFollowerOffset() {
		return this.getActiveState().data.path_follower_offset;
	}

	getFinalGoalOffset() {
		return this.states[this.states.length - 1].data.goal_offset;
	}

	getFinalEndDirection() {
		return this.states[this.states.length - 1].geometry.checkpoints.at(-1)!.tangent;
	}

	getActivePath() {
		return this.getActiveState().geometry.path;
	}

	getFinalEndPosition() {
		return this.states[this.states.length - 1].geometry.getEndPoint();
	}

	getActiveLength() {
		return this.getActiveState().geometry.length;
	}

	getActiveMaxInk() {
		const { geometry } = this.getActiveState();

		return geometry.length * this.maxInkMultiplier;
	}

	getMaximumScore() {
		return this.states.reduce((total, { progress }) => total + progress.getMaximumScore(), 0);
	}

	getScore() {
		return this.progress.getScore();
	}

	isFinalStroke() {
		return this.progress.isFinalStroke();
	}

	traceSegment(
		from: Phaser.Types.Math.Vector2Like,
		to: Phaser.Types.Math.Vector2Like,
		maxMovementProgress: number,
		endpointOnly = false,
	) {
		const result = this.progress.getActive().traceSegment(
			{ x: from.x, y: from.y },
			{ x: to.x, y: to.y },
			{
				maxMovementProgress,
				endpointOnly,
				corridorIntervals: this.getActiveState().geometry.getCorridorIntervals(
					from,
					to,
					this.checkpointMaxDistance,
				),
			},
		);

		this.debugRenderer?.render(this.progress.getActiveIndex());

		return result;
	}

	advanceStroke() {
		this.progress.advance();
		this.guideRenderer.render(this.progress.getActiveIndex());
		this.debugRenderer?.render(this.progress.getActiveIndex());
	}

	revealCheckpoint(checkpointIndex: number, score: number) {
		this.rewardRenderer.reveal(this.progress.getActiveIndex(), checkpointIndex, score);
	}

	reset() {
		this.rewardRenderer.clear();
		this.progress.reset();
		this.guideRenderer.render(this.progress.getActiveIndex());
		this.debugRenderer?.render(this.progress.getActiveIndex());
	}

	private getActiveState() {
		return this.states[this.progress.getActiveIndex()];
	}
}
